/**
 * THE COLUMNAR PROJECTION (0.1.478, item 1 of the perf audit's last ranking).
 *
 * WHY. The Items list reads every row of the catalogue in one call so that its groups are complete
 * (maintainer's ruling). At the atelier's ~4,200 items that projection, sent as one object per row,
 * is 1.39 MB of JSON: the eleven field names repeated 4,200 times, every null spelled out, a
 * `fields` object per row. The first production timing (0.1.477) put the server's share of that
 * call at ~880 ms, of which ~670 ms is Node on one shared vCPU building, serialising and gzipping
 * exactly that JSON — and the phone parses it again on arrival. Measured before this shipped, on
 * 4,167 synthetic rows: objects 1.21 MB / 138 KB gzip / 24 ms to build; columns 512 KB / 79 KB /
 * 11 ms; parse 6.6 ms → 1.9 ms; the decode below 3.4 ms.
 *
 * WHAT. The same rows as COLUMNS: one array per field, in row order. The five heavily repeated
 * fields (status, brand, category, location, source) and every select-kind custom field are
 * DICTIONARY-encoded — a list of distinct values plus one small integer per row, `-1` for null —
 * so 4,167 category names become 83 strings and 4,167 integers. The three that rarely repeat
 * (id, name, photo key) and the two sparse ones (return date, flag reason) stay plain arrays.
 *
 * The API encodes, the web decodes ONCE into the row objects the display engine has always
 * consumed — the engine, the datasheet, the grouped list and windowing never see this shape.
 * Encode and decode live HERE, in the shared package, because the web must not import API
 * runtime code (Vite would bundle pg and Fastify), and one round-trip test can prove the pair.
 */
function dictionary(values) {
    const list = [];
    const at = new Map();
    const index = new Array(values.length);
    for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (v === null || v === undefined) {
            index[i] = -1;
            continue;
        }
        let k = at.get(v);
        if (k === undefined) {
            k = list.length;
            at.set(v, k);
            list.push(v);
        }
        index[i] = k;
    }
    return { values: list, index };
}
function lookup(column, i) {
    const k = column.index[i];
    return k === undefined || k < 0 ? null : (column.values[k] ?? null);
}
export function encodeDisplayColumns(rows) {
    // Field labels in first-seen order, so the decode's `fields` objects list them stably.
    const labels = [];
    const seen = new Set();
    for (const r of rows)
        for (const label of Object.keys(r.fields))
            if (!seen.has(label)) {
                seen.add(label);
                labels.push(label);
            }
    return {
        id: rows.map((r) => r.id),
        name: rows.map((r) => r.name),
        photoKey: rows.map((r) => r.photoKey),
        returnDueOn: rows.map((r) => r.returnDueOn),
        flagReason: rows.map((r) => r.flagReason),
        createdAt: rows.map((r) => r.createdAt),
        lastEditedAt: rows.map((r) => r.lastEditedAt),
        status: dictionary(rows.map((r) => r.status)),
        brand: dictionary(rows.map((r) => r.brand)),
        categoryPath: dictionary(rows.map((r) => r.categoryPath)),
        locationName: dictionary(rows.map((r) => r.locationName)),
        sourceName: dictionary(rows.map((r) => r.sourceName)),
        fields: labels.map((label) => ({ label, ...dictionary(rows.map((r) => r.fields[label] ?? null)) })),
    };
}
export function decodeDisplayColumns(columns) {
    const n = columns.id.length;
    const rows = new Array(n);
    for (let i = 0; i < n; i++) {
        const fields = {};
        for (const f of columns.fields) {
            const v = lookup(f, i);
            if (v !== null)
                fields[f.label] = v;
        }
        rows[i] = {
            id: columns.id[i],
            name: columns.name[i],
            brand: lookup(columns.brand, i),
            // A row always has a status; an absent one would be a broken payload, and '' is what the
            // engine's status axis files under its "(none)" bucket rather than crashing the list.
            status: lookup(columns.status, i) ?? '',
            categoryPath: lookup(columns.categoryPath, i),
            locationName: lookup(columns.locationName, i),
            returnDueOn: columns.returnDueOn[i] ?? null,
            sourceName: lookup(columns.sourceName, i),
            photoKey: columns.photoKey[i] ?? null,
            flagReason: columns.flagReason[i] ?? null,
            // A row always has a creation instant; an absent one is a broken payload, and '' sorts
            // first rather than crashing the list (the status column's own rule).
            createdAt: columns.createdAt[i] ?? '',
            lastEditedAt: columns.lastEditedAt[i] ?? null,
            fields,
        };
    }
    return rows;
}
//# sourceMappingURL=columns.js.map